import React, { useEffect, useState } from 'react';
import './_NewBranch.css';
import { ProjectsModel } from '../models/ProjectsModel';
import { ProjectModel, ActivityStatus, SelectListItem } from '../models/superbit-api-models';

// React port of Views/SystemManagement/Modals/_NewProject.cshtml.
// Handles both add (project undefined) and edit (project set). Both save through
// projectUpsert — the server upserts by Id, exactly like the old form which always
// posted to Project/Upsert. A project has only name / status / project-manager.

type Props = {
    show: boolean;
    // Existing project when editing; undefined when adding.
    project?: ProjectModel;
    onClose: () => void;
    onSaved: () => void;
};

type FormState = {
    id?: string;
    name: string;
    status: ActivityStatus;
    projectManagerId: string;
};

const NewProject = (props: Props) => {
    const { show, project, onClose, onSaved } = props;
    const isEdit = !!(project && project.id);

    const [projectsModel] = useState<ProjectsModel>(() => new ProjectsModel());

    const [form, setForm] = useState<FormState>({
        name: '',
        status: ActivityStatus._0,
        projectManagerId: '',
    });
    const [managers, setManagers] = useState<SelectListItem[]>([]);
    const [errors, setErrors] = useState<{ [k: string]: string }>({});
    const [saving, setSaving] = useState<boolean>(false);
    const [serverError, setServerError] = useState<string>('');

    useEffect(() => {
        if (!show) return;
        setErrors({});
        setServerError('');
        // Seed the form from the project being edited, or a blank add form.
        setForm({
            id: project?.id,
            name: project?.name || '',
            status: project?.status != null ? project.status : ActivityStatus._0,
            projectManagerId: project?.projectManagerId || '',
        });
        loadManagers();
    }, [show, project]);

    const loadManagers = async () => {
        try {
            const list = await projectsModel.getProjectManagers();
            setManagers(list);
        } catch (e) {
            console.error('Failed to load project managers', e);
            setManagers([]);
        }
    };

    const update = (patch: Partial<FormState>) => setForm((f) => ({ ...f, ...patch }));

    const validate = (): boolean => {
        const errs: { [k: string]: string } = {};
        if (!form.name.trim()) errs.name = 'שדה חובה';
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setServerError('');
        if (!validate()) return;

        const body = new ProjectModel();
        body.id = form.id;
        body.name = form.name.trim();
        body.status = form.status;
        body.projectManagerId = form.projectManagerId || undefined;

        setSaving(true);
        try {
            await projectsModel.projectUpsert(body);
            onSaved();
            onClose();
        } catch (err) {
            console.error('Failed to save project', err);
            setServerError('שמירת הפרויקט נכשלה');
        } finally {
            setSaving(false);
        }
    };

    if (!show) return null;

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) onClose();
    };

    const title = isEdit ? 'עריכת פרויקט' : 'פרויקט חדש';

    return (
        <div className="newbranch-overlay" onClick={handleOverlayClick}>
            <div className="newbranch-modal" dir="rtl">
                <form id="addProjectForm" autoComplete="off" onSubmit={onSubmit}>
                    <div className="header">
                        <button type="button" className="close" aria-label="Close" onClick={onClose}>
                            <span className="close-modal" />
                        </button>
                        <h3 className="title">{title}</h3>
                    </div>

                    <div className="create-form__wrappers">
                        <div className="col-sm">
                            <div className="minor-title-box text-right">
                                <span className="title">שם הפרויקט</span>
                            </div>
                            <input
                                className="form-control"
                                placeholder="שם"
                                value={form.name}
                                onChange={(e) => update({ name: e.target.value })}
                            />
                            {errors.name && <span className="text-danger">{errors.name}</span>}
                        </div>
                    </div>

                    <div className="create-form__wrappers">
                        <div className="col-sm">
                            <div className="minor-title-box text-right">
                                <span className="title">סטטוס</span>
                            </div>
                            <select
                                className="form-control"
                                value={form.status}
                                onChange={(e) =>
                                    update({ status: parseInt(e.target.value, 10) as ActivityStatus })
                                }
                            >
                                <option value={ActivityStatus._0}>פעיל</option>
                                <option value={ActivityStatus._1}>לא פעיל</option>
                            </select>
                        </div>
                    </div>

                    <div className="create-form__wrappers">
                        <div className="col-sm">
                            <div className="minor-title-box text-right">
                                <span className="title">מנהל פרויקט</span>
                            </div>
                            <select
                                className="form-control"
                                value={form.projectManagerId}
                                onChange={(e) => update({ projectManagerId: e.target.value })}
                            >
                                <option value="">בחר</option>
                                {managers.map((m) => (
                                    <option key={m.value} value={m.value}>
                                        {m.text}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {serverError && <div className="newbranch-server-error text-danger">{serverError}</div>}

                    <div className="action-buttons">
                        <div className="action-buttons__half">
                            <button
                                type="submit"
                                className="btn btn-link button btn-primary button-outlined"
                                disabled={saving}
                            >
                                {saving ? 'שומר…' : isEdit ? 'שמור' : 'הוסף'}
                            </button>
                            <button
                                type="button"
                                className="btn btn-link button button-outlined"
                                onClick={onClose}
                            >
                                ביטול
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default NewProject;
